/**
 * What tells a teacher that the class on screen is not their class.
 *
 * Two parts, deliberately: a bar that states why sample data is here and offers the one
 * button that undoes any poking around, and a SAMPLE watermark tiled across the page. The
 * bar can be scrolled past — the watermark cannot, which matters because every other pixel
 * of the roster is intentionally identical to the real thing.
 */
import { RotateCcw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { resetSampleData } from '../sample/sampleStore';

/**
 * Tiled "SAMPLE" across the whole viewport, behind nothing and clickable through.
 *
 * `pointer-events-none` is what makes it honest: it is a mark on the page, not a scrim —
 * every button underneath still works exactly as it does on a real roster.
 */
export function SampleWatermark() {
  return (
    <div
      aria-hidden="true"
      data-testid="sample-watermark"
      className="pointer-events-none fixed inset-0 z-[45] overflow-hidden select-none"
    >
      <div className="absolute inset-0 -rotate-[24deg] scale-150 flex flex-col justify-around">
        {Array.from({ length: 8 }).map((_, row) => (
          <div key={row} className="flex justify-around">
            {Array.from({ length: 4 }).map((__, col) => (
              <span
                key={col}
                className="text-3xl sm:text-5xl font-extrabold tracking-[0.3em] text-gray-900/[0.06] whitespace-nowrap"
              >
                SAMPLE
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The bar above the roster. It keeps the sentence the empty state used to show ("No
 * programs meet today") rather than dropping it, because that fact is still the reason the
 * teacher is looking at a made-up class.
 */
export function SampleModeBanner() {
  const queryClient = useQueryClient();

  function handleReset() {
    resetSampleData();
    // Same refresh path a real change takes, so the roster re-reads the (reset) day.
    void queryClient.invalidateQueries({ queryKey: ['attendance'] });
  }

  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5">
      <span className="rounded-md bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
        Sample
      </span>
      <p className="min-w-0 flex-1 text-xs leading-snug text-amber-900">
        No programs meet today, so this is a made-up class you can try everything on.
        Check-ins stay in this browser — nothing is saved to BHCS.
      </p>
      <button
        type="button"
        onClick={handleReset}
        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100"
      >
        <RotateCcw className="h-3.5 w-3.5" />
        Reset sample
      </button>
    </div>
  );
}
